import { Config } from '@/types/config';
import { AppointRequest } from '@/types/requests';
import { Step } from '@/types/step';
import { jsonCopy, safeParse } from '@/utils/json';

// The insurance-review answer carries card PHI — member name, member id, group
// number — in each plan's `fields`. The session store persists history to the
// practice-origin localStorage to survive a formAction navigation, and
// practice-page scripts can read web storage, so that PHI must not land there.
// Strip it before persisting, mirroring the card-image strip the answer
// already does upstream. Operates on a jsonCopy, so the live history keeps its
// data for the flow (PHI8).
//
// Scope: card fields only, per the PHI8 finding. Other answers in the same
// history entry (e.g. demographics steps) persist as they always have; that
// is pre-existing behaviour of the formAction history store, tracked apart
// from this strip.
function stripInsurancePhi (history: Step[]): Step[] {
    for (const step of history) {
        for (const question of step.questions || []) {
            const value = question.value as unknown as { insurances?: Array<Record<string, unknown>> };
            if (question.type !== 'insuranceReview') {
                continue;
            }
            if (value && Array.isArray(value.insurances)) {
                for (const ins of value.insurances) {
                    delete ins.fields;
                }
            } else {
                // A PHI control that silently no-ops on an unexpected shape is
                // worse than one that says so: history is set as an object
                // (flow.ts) and JSON round-trips keep it one, so anything else
                // here means the answer shape drifted and the strip missed it.
                // eslint-disable-next-line no-console
                console.warn('[odr] insuranceReview answer has an unexpected shape; PHI strip skipped', typeof question.value);
            }
        }
    }
    return history;
}

export function storeSession (appConfig: Config, history: Step[], data: AppointRequest) {
    if (data.formAction) {
        window.localStorage.setItem(`__odr_session:${appConfig.index}`, JSON.stringify({
            data: stripInsurancePhi(jsonCopy(history)),
            expiry: Date.now() + appConfig.storeSessionTTL,
        }));
    }
}

export function getStoredSession (appConfig: Config): Step[] | null {
    let storedSessionRaw = window.localStorage.getItem(`__odr_session:${appConfig.index}`);
    let storedSession;
    if (storedSessionRaw !== null) {
        storedSession = safeParse(storedSessionRaw);
        if (storedSession.data && storedSession.expiry > Date.now()) {
            return storedSession.data;
        }
    };
    return null;
}

export function deleteSession (index: number) {
    window.localStorage.removeItem(`__odr_session:${index}`);
}
